/**
 * Provider Context
 * Global state management for cloud provider selection
 */

import React, { createContext, useState, useContext, useEffect } from 'react';
import ProviderService from '../services/providers/ProviderService';

const ProviderContext = createContext();

export const useProvider = () => {
  const context = useContext(ProviderContext);
  if (!context) {
    throw new Error('useProvider must be used within a ProviderProvider');
  }
  return context;
};

export const ProviderProvider = ({ children }) => {
  const [selectedProvider, setSelectedProvider] = useState(() => {
    // Initialize from localStorage or default to digitalocean
    return localStorage.getItem('selectedProvider') || 'digitalocean';
  });

  const [availableProviders, setAvailableProviders] = useState([]);
  const [resourceTypes, setResourceTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load available providers on mount
  useEffect(() => {
    const loadProviders = async () => {
      try {
        const providers = await ProviderService.getProviders();
        setAvailableProviders(providers);
      } catch (error) {
        console.error('Error loading providers:', error);
        setAvailableProviders([]);
      } finally {
        setLoading(false);
      }
    };

    loadProviders();
  }, []);

  // Load resource types when provider changes
  useEffect(() => {
    const loadResourceTypes = async () => {
      if (!selectedProvider) return;
      
      try {
        const types = await ProviderService.getResourceTypes(selectedProvider);
        setResourceTypes(types);
      } catch (error) {
        console.error('Error loading resource types:', error);
        setResourceTypes([]);
      }
    };

    loadResourceTypes();
  }, [selectedProvider]);

  // Persist provider selection to localStorage
  const handleSetProvider = (providerId) => {
    setSelectedProvider(providerId);
    localStorage.setItem('selectedProvider', providerId);
  };

  const value = {
    // Current provider
    selectedProvider,
    setSelectedProvider: handleSetProvider,
    
    // Available options
    availableProviders,
    resourceTypes,
    
    // State
    loading,
    
    // Helpers
    isProviderAvailable: ProviderService.isProviderAvailable.bind(ProviderService),
    getProvider: ProviderService.getProvider.bind(ProviderService),
  };

  return (
    <ProviderContext.Provider value={value}>
      {children}
    </ProviderContext.Provider>
  );
};

export default ProviderContext;

